// EducationCertifications.jsx
import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  IconButton,
} from "@mui/material";
import { School, CardMembership, Add, Edit, Delete } from "@mui/icons-material";
import EducationDialog from "./EducationDialog";
import CertificationDialog from "./CertificationDialog";
import {
  certificationToFormValues,
  formatCertificationDateRange,
} from "../../../utils/profileFieldNormalization";

export default function EducationCertifications({
  educationList,
  setEducationList,
  certificationList,
  setCertificationList,
  hasProfileChanges,
  setHasProfileChanges,
  disabled,
}) {
  const [educationDialogOpen, setEducationDialogOpen] = useState(false);
  const [certDialogOpen, setCertDialogOpen] = useState(false);

  const [editingEducation, setEditingEducation] = useState(null);
  const [editingCertification, setEditingCertification] = useState(null);

  const [educationForm, setEducationForm] = useState({
    degree: "",
    fieldOfStudy: "",
    location: "",
    declaration: false,
  });

  const [certForm, setCertForm] = useState({
    certificationName: "",
    issuingOrganization: "",
    issueDate: "",
    expiryDate: "",
    noExpiry: false,
    credentialId: "",
    credentialUrl: "",
    declaration: false,
  });

  const handleSaveEducation = (data) => {
    if (!disabled) {
      setHasProfileChanges(true);
    }
    if (editingEducation) {
      setEducationList((prev) =>
        prev.map((e) =>
          e.id === editingEducation ? { ...data, id: e.id } : e,
        ),
      );
    } else {
      setEducationList([...educationList, { ...data, id: Date.now() }]);
    }
    setEducationDialogOpen(false);
  };

  const handleSaveCertification = (data) => {
    if (!disabled) {
      setHasProfileChanges(true);
    }

    if (editingCertification) {
      setCertificationList((prev) =>
        prev.map((c) =>
          c.id === editingCertification ? { ...data, id: c.id } : c,
        ),
      );
    } else {
      setCertificationList([...certificationList, { ...data, id: Date.now() }]);
    }
    setCertDialogOpen(false);
  };

  return (
    <Box sx={{ mx: "auto", mt: 5 }}>
      {/* Education */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <School sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Education *
            </Typography>
          </Box>

          {educationList?.length > 0 &&
            educationList?.map((edu) => (
              <Box key={edu.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">{edu.degree}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {edu.fieldOfStudy}
                </Typography>

                <Box>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => {
                      setEditingEducation(edu.id);
                      // Convert terms_accepted back to declaration for form
                      const { terms_accepted, ...restEdu } = edu;
                      const formData = {
                        ...restEdu,
                        declaration:
                          terms_accepted === 1 ||
                          terms_accepted === true ||
                          terms_accepted === "1",
                      };
                      setEducationForm(formData);
                      setEducationDialogOpen(true);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={disabled}
                    onClick={() => {
                      if (!disabled) {
                        setHasProfileChanges(true);
                      }
                      setEducationList((prev) =>
                        prev.filter((e) => e.id !== edu.id),
                      );
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}

          <Button
            startIcon={<Add />}
            disabled={disabled}
            onClick={() => {
              setEditingEducation(null);
              setEducationForm({
                degree: "",
                fieldOfStudy: "",
                location: "",
                declaration: false,
              });
              setEducationDialogOpen(true);
            }}
          >
            Add Education
          </Button>
        </CardContent>
      </Card>

      {/* Certifications */}
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CardMembership sx={{ mr: 1 }} />
            <Typography variant="h6" sx={{ flexGrow: 1 }}>
              Certifications
            </Typography>
          </Box>

          {certificationList?.length > 0 &&
            certificationList.map((cert) => (
              <Box key={cert.id} sx={{ mb: 2 }}>
                <Typography variant="subtitle1">
                  {cert.certificationName || cert.certification_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {cert.issuingOrganization || cert.issuing_organization}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatCertificationDateRange(cert)}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    disabled={disabled}
                    onClick={() => {
                      setEditingCertification(cert.id);
                      setCertForm(certificationToFormValues(cert));
                      setCertDialogOpen(true);
                    }}
                  >
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    variant="outlined"
                    color="error"
                    disabled={disabled}
                    onClick={() => {
                      if (!disabled) {
                        setHasProfileChanges(true);
                      }
                      setCertificationList((prev) =>
                        prev.filter((c) => c.id !== cert.id),
                      );
                    }}
                  >
                    <Delete fontSize="small" />
                  </IconButton>
                </Box>
              </Box>
            ))}

          <Button
            startIcon={<Add />}
            disabled={disabled}
            onClick={() => {
              setEditingCertification(null);
              setCertForm({
                certificationName: "",
                issuingOrganization: "",
                issueDate: "",
                expiryDate: "",
                noExpiry: false,
                credentialId: "",
                credentialUrl: "",
                declaration: false,
              });
              setCertDialogOpen(true);
            }}
          >
            Add Certification
          </Button>
        </CardContent>
      </Card>

      {/* Separate Dialog Components */}
      <EducationDialog
        open={educationDialogOpen}
        onClose={() => setEducationDialogOpen(false)}
        onSave={handleSaveEducation}
        formData={educationForm}
        setFormData={setEducationForm}
        editingId={editingEducation}
        educationList={educationList}
      />

      <CertificationDialog
        open={certDialogOpen}
        onClose={() => setCertDialogOpen(false)}
        onSave={handleSaveCertification}
        formData={certForm}
        setFormData={setCertForm}
        editingId={editingCertification}
        certificationList={certificationList}
      />
    </Box>
  );
}
